import { _notNil } from '@/littledash';
import type { State } from '@/model/State.model';
import { web as webRoute } from '@/support/route';
import { useDevices } from '@/utils/devices/useDevices';
import type { FC } from 'react';
import {
  RiCalendarLine,
  RiCheckboxMultipleLine,
  RiDraftLine,
  RiErrorWarningFill,
  RiFile3Line,
  RiGpsLine,
  RiTestTubeLine,
} from 'react-icons/ri';
import animalColonyPaw from '@/assets/images/animal-colony-paw.svg';
import { useDispatch, useSelector } from 'react-redux';
import NavButton from './NavButton/NavButton';
import UserMenu from './UserMenu/UserMenu';
import styles from './Sidebar.module.scss';

/**
 * Primary vertical navigation sidebar rendered on the left edge of the application.
 */
const Sidebar: FC = () => {
  const currentStudyDetails = useSelector((state: State) => state.ui.currentStudyDetails);
  const dispatch = useDispatch();
  const { unmappedDevices } = useDevices({});

  const openDeviceConnectionsModal = () => {
    dispatch({
      type: 'OPEN_MODAL',
      modal: 'DEVICE_CONNECTIONS',
      props: { study: currentStudyDetails },
    });
  };

  return (
    <div className={`bg-branding-color white ${styles.container}`} data-testid="sidebar">
      <div className={styles.section}>
        <NavButton to={webRoute('dashboard')} activeMatch={['/studies']} tooltip="Studies" testId="nav-studies">
          <RiFile3Line />
        </NavButton>
        <NavButton to={webRoute('colony')} tooltip="Colony" testId="nav-colony">
          <img src={animalColonyPaw} alt="Colony" className={styles.icon} />
        </NavButton>
        <NavButton to={webRoute('samples')} tooltip="Samples" testId="nav-samples">
          <RiTestTubeLine />
        </NavButton>
        <NavButton to={webRoute('tasks')} tooltip="Tasks" testId="nav-tasks">
          <RiCalendarLine />
        </NavButton>
        <NavButton to={webRoute('approvals')} tooltip="Reviews" testId="nav-reviews">
          <RiCheckboxMultipleLine />
        </NavButton>
        <NavButton to={webRoute('requests')} tooltip="Requests" testId="nav-requests">
          <RiDraftLine />
        </NavButton>
      </div>

      <div className={styles.section}>
        {_notNil(navigator.serial) && (
          <NavButton onClick={openDeviceConnectionsModal} tooltip="Devices" testId="nav-devices">
            <RiGpsLine />
            {unmappedDevices > 0 && (
              <RiErrorWarningFill style={{ position: 'absolute', left: '28px', top: '8px' }} color="red" />
            )}
          </NavButton>
        )}
        <UserMenu />
      </div>
    </div>
  );
};

export default Sidebar;
